import { useParams, Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Button } from '@mui/material';
import { psiTokens } from '../theme/psiTheme';
import { getClassRecord } from '../data/classes';
import { getCertificateType, getCertificateHolders } from '../data/certificateTypes';
import { ClassRecordContent } from './ClassRecord';
import { CertificateTypeContent } from './CertificateTypeRecord';
import { usePageMeta } from '../hooks/usePageMeta';

// Each branch below is its own component so its usePageMeta() call has a
// stable, single home: DynamicRecordPage itself can't call it directly
// (three different branches with three different early returns would give
// the hook a different call order per render, which breaks the Rules of
// Hooks), and CertificateTypeContent deliberately doesn't call it either
// (see the comment in CertificateTypeRecord.jsx, it's reused for the
// multi-type /certificates listing where per-instance meta would clobber
// itself).
function SingleCertificateTypePage({ type, holders }) {
  usePageMeta(`${type.programName} Certification`, type.description);
  return <CertificateTypeContent type={type} holders={holders} />;
}

function NotFoundPage({ slug }) {
  usePageMeta('Page Not Found', `We couldn't find a record for "${slug}".`, { noindex: true });
  return (
    <Container maxWidth="xl" sx={{ py: 12, textAlign: 'center' }}>
      <Typography variant="h3" sx={{ color: psiTokens.text.primary, fontWeight: 700, mb: 2 }}>
        Page not found.
      </Typography>
      <Typography sx={{ color: psiTokens.text.tertiary, mb: 4 }}>
        We couldn't find a record for "{slug}".
      </Typography>
      <Button component={RouterLink} to="/" variant="contained">
        Back to Home
      </Button>
    </Container>
  );
}

// Resolves any top-level path (e.g. /GB250814, /GreenBeltCertificate)
// against the class registry first, then the certificate-type registry, so
// neither a new class nor a new certificate track ever needs its own route
// declared in App.jsx: just a new data file under src/data/.
export default function DynamicRecordPage() {
  const { slug } = useParams();

  const classRecord = getClassRecord(slug);
  if (classRecord) {
    return <ClassRecordContent record={classRecord} />;
  }

  const certificateType = getCertificateType(slug);
  if (certificateType) {
    return <SingleCertificateTypePage type={certificateType} holders={getCertificateHolders(slug)} />;
  }

  return <NotFoundPage slug={slug} />;
}
